import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {MatSnackBar} from "@angular/material/snack-bar";
import {FormService} from "@core/shared/services/form.service";
import {SocietyDistributor} from "@core/shared/models/society/society-distributor";
import {SocietyDistributorService} from "@core/shared/services/society/society-distributor.service";
import {TranslateService} from "@ngx-translate/core";
import {Address} from "@core/shared/models/address";
import {Observable} from "rxjs";
import {OfferCatalog, OfferCatalogStatusType} from "@core/shared/models/offer/offer-catalog";
import {OfferCatalogService} from "@core/shared/services/offer/offer-catalog.service";
import {FilterBuilder} from "@core/shared/models/filter";
import {TextFilterField} from "@core/shared/models/filter/text-filter-field";
import {Offer} from "@core/shared/models/offer";
import {map} from "rxjs/operators";
import {MatTabGroup} from "@angular/material/tabs";
import {TranslationService} from "@core/shared/services/translation.service";
import {SocietyCommission} from "@core/shared/models/society-commission";

@Component({
    selector: 'app-core-page-society-distributor-update',
    templateUrl: './page-society-distributor-update.component.html',
    styleUrls: ['./page-society-distributor-update.component.scss'],
    providers: [
        FormService
    ],
    standalone: false
})
export class PageSocietyDistributorUpdateComponent implements OnInit, AfterViewInit {

    @ViewChild('tabGroup', {static: true}) tabGroup: MatTabGroup;

    public societyDistributor: SocietyDistributor;

    public acceptedOffers$: Observable<Offer[]>;

    public refusedOffers$: Observable<Offer[]>;

    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _formBuilder: UntypedFormBuilder,
        private _snackBar: MatSnackBar,
        private _translateService: TranslateService,
        private _societyDistributorService: SocietyDistributorService,
        private _offerCatalogService: OfferCatalogService,
        public formService: FormService,
        public translationService: TranslationService
    ) {
    }

    ngOnInit(): void {

        this._activatedRoute.data.subscribe((data: { societyDistributor: SocietyDistributor }): void => {

            this.societyDistributor = data.societyDistributor;

            this.acceptedOffers$ = this._loadOffers('accepted');

            this.refusedOffers$ = this._loadOffers('refused');

            this._initForm();

            this.form.patchValue(this.societyDistributor);
        });
    }

    ngAfterViewInit(): void {

        this._activatedRoute.fragment.subscribe((fragment: ('availableOffers')): void => {

            switch (fragment) {

                case 'availableOffers':

                    this.tabGroup.selectedIndex = 2;

                    break;
            }
        });
    }

    private _loadOffers(status: string): Observable<Offer[]> {

        const filterBuilder: FilterBuilder = new FilterBuilder();

        filterBuilder.addField(new TextFilterField('society.id', 'eq', this.societyDistributor.distributor.id));

        filterBuilder.addField(new TextFilterField('status', 'eq', status as OfferCatalogStatusType));

        const params: string[] = filterBuilder.serializedFilters;

        return this._offerCatalogService.getItemsRequestAPI(params).pipe(
            map((items: OfferCatalog[]): Offer[] => {

                return items.map((item: OfferCatalog): Offer => {

                    return item.offer;
                });
            })
        );
    }

    private _initForm(): void {

        this.formService.form = this._formBuilder.group({
            hasAccessOffers: [true, Validators.required]
        });

        this.formService.submitCallback = (): void => {

            const data: object = this.form.value;

            this._societyDistributorService.updateItemAPI(this._activatedRoute.snapshot.params['id'], data).subscribe((): void => {

                this._snackBar.open(this._translateService.instant('society.distributor.update.success.value'), this._translateService.instant('notification.close.action.value'), {
                    duration: 5000
                });

                this.redirectToList();
            });
        };
    }

    public redirectToList(): void {

        this._router.navigate(['account/society/distributor/list']);
    }

    get commissionIndividualStay(): string {

        if(!this.isAvailableCommission) {

            return 'N/A';
        }

        const commission: SocietyCommission = this.societyDistributor.distributor.commission;

        if(!commission.enable){

            return 'N/A';
        }

        const value: string = (commission.individualStay * 100).toFixed(2);

        return `${value} % ${this._translateService.instant('tax.excluded.value')}`;
    }

    get commissionIndividualDay(): string {

        if(!this.isAvailableCommission) {

            return 'N/A';
        }

        const commission: SocietyCommission = this.societyDistributor.distributor.commission;

        if(!commission.enable){

            return 'N/A';
        }

        const value: string = (commission.individualDay * 100).toFixed(2);

        return `${value} % ${this._translateService.instant('tax.excluded.value')}`;
    }

    get commissionGroupStay(): string {

        if(!this.isAvailableCommission) {

            return 'N/A';
        }

        const commission: SocietyCommission = this.societyDistributor.distributor.commission;

        if(!commission.enable){

            return 'N/A';
        }

        const value: string = (commission.groupStay * 100).toFixed(2);

        return `${value} % ${this._translateService.instant('tax.excluded.value')}`;
    }

    get commissionGroupDay(): string {

        if(!this.isAvailableCommission) {

            return 'N/A';
        }

        const commission: SocietyCommission = this.societyDistributor.distributor.commission;

        if(!commission.enable){

            return 'N/A';
        }

        const value: string = (commission.groupDay * 100).toFixed(2);

        return `${value} % ${this._translateService.instant('tax.excluded.value')}`;
    }

    get incomingPayment(): string {

        if(!this.isAvailableCommission) {

            return 'N/A';
        }

        const commission: SocietyCommission = this.societyDistributor.distributor.commission;

        return this._translateService.instant(`${commission.incomingPayment ? 'yes' : 'no'}.value`);
    }

    get isAvailableCommission(): boolean {

        return this.societyDistributor.distributor.commission !== null;
    }

    get form(): UntypedFormGroup {

        return this.formService.form;
    }

    get localeId(): string {

        return this._translateService.currentLang;
    }

    get addressFields(): Address {

        return this.societyDistributor.distributor.addresses.find((address: Address): boolean => {

            return address.type === 'mailing';

        }) || this.societyDistributor.distributor.addresses[0];
    }
}
